import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, ArrowRight, Building2, HelpCircle, Bot, Send, X, AlertTriangle,
  CheckCircle, Loader, MessageSquare, Shield
} from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';

const KybQuestionFlow = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [question, setQuestion] = useState(null);
  const [answer, setAnswer] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showAiHelp, setShowAiHelp] = useState(false);
  const [aiMessage, setAiMessage] = useState('');
  const [aiConversation, setAiConversation] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [sessionFlagged, setSessionFlagged] = useState(false);

  const fetchSession = useCallback(async () => {
    try {
      const res = await api.get(`/kyb-levels/sessions/${id}`);
      if (res.data.session_id) {
        // Response format is question format
        setQuestion(res.data);
        // Fetch session details separately
        const detailRes = await api.get(`/kyb-levels/sessions/${id}/detail`);
        setSession(detailRes.data.session);
        setSessionFlagged(detailRes.data.session?.flagged_for_review || false);
      } else if (res.data.session) {
        setSession(res.data.session);
        setQuestion(res.data);
        setSessionFlagged(res.data.session?.flagged_for_review || false);
      }
    } catch (error) {
      console.error('Error fetching session:', error);
      toast.error('Failed to load verification session');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchSession(); }, [fetchSession]);

  const submitAnswer = async () => {
    if (!answer && question?.question_type !== 'checks') return;
    try {
      setSubmitting(true);
      const res = await api.post(`/kyb-levels/sessions/${id}/answer`, { answer });
      if (res.data.status === 'COMPLETED' || res.data.status === 'APPROVED' || res.data.status === 'REJECTED') {
        toast.success('Verification complete!');
        navigate(`/kyb-sessions/${id}`);
      } else {
        setAnswer('');
        setAiConversation([]);
        fetchSession();
      }
    } catch (error) {
      toast.error(error.response?.data?.error?.message || 'Failed to submit answer');
    } finally {
      setSubmitting(false);
    }
  };

  const askAiHelp = async () => {
    if (!aiMessage.trim()) return;
    try {
      setAiLoading(true);
      const res = await api.post(`/kyb-levels/sessions/${id}/ai-help`, {
        node_id: question?.node_id,
        message: aiMessage
      });
      
      setAiConversation(prev => [...prev, 
        { role: 'user', content: aiMessage },
        { role: 'assistant', content: res.data.message, clarifications: res.data.clarifications }
      ]);
      setAiMessage('');

      if (res.data.session_flagged) {
        setSessionFlagged(true);
        toast.error('Session flagged for review due to suspicious activity');
      }
    } catch (error) {
      toast.error(error.response?.data?.error?.message || 'Failed to get help');
    } finally {
      setAiLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!session || session.status === 'COMPLETED' || session.status === 'APPROVED' || session.status === 'REJECTED') {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8 text-center">
        <CheckCircle className="w-16 h-16 mx-auto text-green-600 mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Verification Complete</h2>
        <p className="text-gray-600 mb-4">This verification session has been completed.</p>
        <button onClick={() => navigate('/kyb-sessions')} className="px-4 py-2 bg-blue-600 text-white rounded-lg">
          View Sessions
        </button>
      </div>
    );
  }

  const getProgressPercent = () => {
    const completed = session?.completed_nodes?.length || 0;
    const total = 30; // Approximate total nodes
    return Math.min(Math.round((completed / total) * 100), 95);
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-6">
        <button onClick={() => navigate(-1)} className="text-gray-500 hover:text-gray-700 mb-2 flex items-center">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </button>
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Building2 className="w-6 h-6 text-blue-600 mr-2" />
            <h1 className="text-xl font-bold text-gray-900">{session?.company_name}</h1>
          </div>
          <span className="text-sm text-gray-500">{getProgressPercent()}% complete</span>
        </div>
        <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
          <div className="h-full bg-blue-600 transition-all" style={{ width: `${getProgressPercent()}%` }} />
        </div>
      </div>

      {/* Flagged Warning */}
      {sessionFlagged && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
          <AlertTriangle className="w-5 h-5 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-semibold text-red-800">Session Under Review</h4>
            <p className="text-sm text-red-700">This session has been flagged for manual review. AI help is disabled.</p>
          </div>
        </div>
      )}

      {/* Question Card */}
      {question && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center">
              <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded mr-2">
                {question.node_id}
              </span>
              <span className="text-xs text-gray-500">{question.stage?.replace('_', ' ')}</span>
            </div>
            {!sessionFlagged && (
              <button
                onClick={() => setShowAiHelp(!showAiHelp)}
                className="flex items-center px-3 py-1.5 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
              >
                <Bot className="w-4 h-4 mr-1" />
                <span className="text-sm">Ask AI for Help</span>
              </button>
            )}
          </div>

          <h2 className="text-lg font-semibold text-gray-900 mb-4">{question.question}</h2>

          {question.help_text && (
            <p className="text-sm text-gray-600 mb-4 p-3 bg-gray-50 rounded-lg">
              <HelpCircle className="w-4 h-4 inline mr-1" /> {question.help_text}
            </p>
          )}

          {/* Answer Input based on question type */}
          <div className="space-y-3 mb-6">
            {question.question_type === 'single_select' && question.options?.map((opt, i) => (
              <button
                key={i}
                onClick={() => setAnswer(opt.value)}
                className={`w-full p-4 text-left rounded-lg border-2 transition-all ${
                  answer === opt.value ? 'border-blue-600 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <span className="font-medium">{opt.label}</span>
                {opt.score_delta > 0 && <span className="ml-2 text-xs text-orange-600">(+{opt.score_delta} risk)</span>}
              </button>
            ))}

            {question.question_type === 'multi_select' && question.options?.map((opt, i) => {
              const selected = Array.isArray(answer) ? answer.includes(opt.value) : false;
              return (
                <button
                  key={i}
                  onClick={() => {
                    const current = Array.isArray(answer) ? answer : [];
                    setAnswer(selected ? current.filter(v => v !== opt.value) : [...current, opt.value]);
                  }}
                  className={`w-full p-4 text-left rounded-lg border-2 transition-all flex items-center ${
                    selected ? 'border-blue-600 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className={`w-5 h-5 rounded border-2 mr-3 flex items-center justify-center ${
                    selected ? 'border-blue-600 bg-blue-600' : 'border-gray-300'
                  }`}>
                    {selected && <CheckCircle className="w-3 h-3 text-white" />}
                  </div>
                  <span className="font-medium">{opt.label}</span>
                </button>
              );
            })}

            {question.question_type === 'data_capture' && (
              <div className="space-y-4">
                {question.fields?.map((field, i) => (
                  <div key={i}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {field.field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      {field.required && <span className="text-red-500 ml-1">*</span>}
                    </label>
                    <input
                      type="text"
                      value={(typeof answer === 'object' ? answer[field.field] : '') || ''}
                      onChange={(e) => setAnswer(prev => ({ ...(typeof prev === 'object' ? prev : {}), [field.field]: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                ))}
              </div>
            )}

            {question.question_type === 'checks' && (
              <div className="p-4 bg-green-50 rounded-lg text-center">
                <Shield className="w-8 h-8 text-green-600 mx-auto mb-2" />
                <p className="text-sm text-green-800">Automated checks will be performed when you continue.</p>
              </div>
            )}
          </div>

          {/* Submit Button */}
          <button
            onClick={submitAnswer}
            disabled={submitting || (!answer && question.question_type !== 'checks')}
            className="w-full py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {submitting ? <Loader className="w-5 h-5 animate-spin" /> : <>Continue <ArrowRight className="w-4 h-4 ml-2" /></>}
          </button>
        </div>
      )}

      {/* AI Help Panel */}
      {showAiHelp && !sessionFlagged && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-4 bg-purple-50 border-b flex items-center justify-between">
            <div className="flex items-center">
              <Bot className="w-5 h-5 text-purple-600 mr-2" />
              <span className="font-medium text-purple-900">AI Help Assistant</span>
            </div>
            <button onClick={() => setShowAiHelp(false)} className="p-1 hover:bg-purple-100 rounded">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="p-4 max-h-80 overflow-y-auto space-y-3">
            {aiConversation.length === 0 && (
              <p className="text-sm text-gray-500 text-center py-4">
                Ask me anything about this question. I can help explain what's being asked, but I cannot provide answers.
              </p>
            )}
            {aiConversation.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] p-3 rounded-lg ${
                  msg.role === 'user' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-800'
                }`}>
                  <p className="text-sm">{msg.content}</p>
                  {msg.clarifications?.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-gray-200 text-xs text-gray-600">
                      <p className="font-medium mb-1">You might also want to know:</p>
                      {msg.clarifications.map((c, j) => <p key={j}>• {c}</p>)}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {aiLoading && (
              <div className="flex justify-start">
                <div className="bg-gray-100 p-3 rounded-lg">
                  <Loader className="w-4 h-4 animate-spin text-gray-500" />
                </div>
              </div>
            )}
          </div>

          <div className="p-4 border-t flex gap-2">
            <input
              type="text"
              value={aiMessage}
              onChange={(e) => setAiMessage(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && askAiHelp()}
              placeholder="Ask a question about this field..."
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
            />
            <button
              onClick={askAiHelp}
              disabled={aiLoading || !aiMessage.trim()}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default KybQuestionFlow;

